// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import Tooltip from "@synd/MobiusOverrides/Tooltip/Tooltip";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { mergeWithCss, sharedBadgeStyle } from "@synd/XDL/Utilities";
import * as React from "react";
import styled, { css } from "styled-components";

export enum BadgeTypeValues {
    Image = "Image",
    Text = "Text",
}

export interface BadgePresentationProps {
    textBadgeCss?: InjectableCss;
    imageBadgeProps?: JSX.IntrinsicElements["img"];
    // XNG-Changes
    textBadgeWrapperCss?: InjectableCss;
}

const TextBadge = styled.div<{ backgroundColor: string; color: string; badgeTextRounding: number }>`
    text-align: center;
    // XNG-Change
    padding: 4px 6px;
    background-color: #${props => props.backgroundColor};
    color: #${props => props.color};
    border-radius: ${props => props.badgeTextRounding}px;
    // XNG-Change
    display: flex;
    ${injectCss}
`;

// XNG-Change
const imageBadge: LazyImageProps = {
    css: css`
        aspect-ratio: 1;
        img {
            width: revert-layer;
        }
    `,
};

const imageNameWrapper = {
    css: css`
        display: flex;
        column-gap: 8px;
        font-size: ${fontConfig.xSmallSingleLine.size}px;
        line-height: ${fontConfig.xSmallSingleLine.lineHeight};
    `,
};

export type BadgeProps = {
    /** When type is Image an img tag will be rendered with the src being the value prop.
     * When the type is text a textbox will be rendered with the given value used as text and color scheme
     * denoted in badgeTextColor and badgeBackgroundColor
     * When no type is given or no corresponding value is given, nothing will render.
     */
    type: BadgeTypeValues;
    /** URL image path or badge text */
    value?:
        | string
        | React.DetailedReactHTMLElement<{}, HTMLElement>
        | React.DetailedReactHTMLElement<{}, HTMLElement>[];
    /** Alt text for the image badge */
    imageAltText?: string;
    /** Hex code for badge text color
     * Default is white (FFFFFF)
     */
    badgeTextColor?: string;
    /** Hex code for badge background color
     * Default is black (000000)
     */
    badgeBackgroundColor?: string;
    /** Number used to determine border radius for text badge in pixels
     * Default is 0
     */
    badgeTextRounding?: number;
    // XNG-Changes
    name?: string;
    displayName?: boolean;
} & BadgePresentationProps;

const Badge = ({
    type,
    value,
    imageAltText,
    badgeTextColor,
    badgeBackgroundColor,
    badgeTextRounding,
    imageBadgeProps,
    textBadgeCss,
    // XNG-Change
    name,
    textBadgeWrapperCss,
    displayName,
}: BadgeProps) => {
    if (!type || !value) {
        return null;
    }

    if (type === BadgeTypeValues.Image) {
        // XNG-Change: on pdp, both badge image and name is displayed
        if (name && displayName) {
            return (
                <StyledWrapper {...imageNameWrapper}>
                    <LazyImage imgProps={imageBadgeProps} src={value as string} altText={imageAltText} />
                    {name}
                </StyledWrapper>
            );
        }
        // XNG-Changes: add tooltip with name siteMessage
        if (name) {
            return (
                <Tooltip
                    triggerComponent={
                        <LazyImage
                            {...imageBadge}
                            imgProps={imageBadgeProps}
                            src={value as string}
                            altText={imageAltText}
                        />
                    }
                    text={name as string}
                />
            );
        }
        return <LazyImage imgProps={imageBadgeProps} src={value as string} altText={imageAltText} />;
    }

    if (type === BadgeTypeValues.Text) {
        // XNG-Change
        const textCss = mergeWithCss(sharedBadgeStyle.badgeText, textBadgeCss);
        return (
            <TextBadge
                backgroundColor={badgeBackgroundColor || "000000"}
                color={badgeTextColor || "FFFFFF"}
                badgeTextRounding={badgeTextRounding || 8}
                {...textBadgeWrapperCss}
            >
                <Typography {...textCss}>{value}</Typography>
            </TextBadge>
        );
    }

    return <>Unknown badge type</>;
};

/** @component */
export default Badge;
